/**
 * Pipeline promotion service context.
 *
 * VITE_PROMOTION_SERVICE controls the adapter:
 *   http | stride  — HttpPromotionService; calls /api/pipelines/:slug/promote*
 *   mock           — MockPromotionService; in-memory, nothing persists
 *   unset          — mock, for local development
 *
 * ── The bug this shape allowed ──
 * The Dockerfile bakes `VITE_PROMOTION_SERVICE=http`. This resolver only accepted `'stride'`, so `'http'`
 * fell through a `default:` arm to the MOCK — in production. A promotion therefore reported a merged PR
 * (#1001, invented), handed back a fabricated run id (`run-mock-promo-1`), and left the deploy step
 * spinning for ever; asking the API for that run's errors 404'd, which the UI showed as "couldn't load
 * deployment errors for this run". Nothing was promoted and nothing said so.
 *
 * One word's difference between the Dockerfile and this file, and the failure mode was a product that
 * pretended to work. Two changes make that impossible rather than unlikely:
 *
 *   1. MOCK IS OPT-IN. An unrecognised value resolves to the REAL service and logs loudly. Previously any
 *      typo — and any new flag value — silently meant mock, which fails in the direction that looks fine.
 *   2. The env is a PARAMETER, so both branches are reachable in a test. A module-scope read cannot be
 *      varied, so it ships untested (see the CLAUDE.md rule this violated).
 *
 * `promotionFlagAgreesWithDockerfile` in the guard test cross-checks the accepted values here against the
 * value the image actually bakes.
 */

import { createContext, useContext, type ReactNode } from 'react'
import type { IPromotionService } from './IPromotionService.interface'
import { MockPromotionService } from './MockPromotionService'
import { HttpPromotionService } from './HttpPromotionService'

/** Both spellings are accepted: the Dockerfile says `http`, older docs and .env files say `stride`. */
const REAL_KEYS = ['http', 'stride'] as const

export function resolvePromotionService(
  env: Record<string, unknown> = import.meta.env as unknown as Record<string, unknown>,
): IPromotionService {
  const raw = env['VITE_PROMOTION_SERVICE']
  const key = typeof raw === 'string' ? raw.trim().toLowerCase() : undefined

  if (key === undefined || key === '') return new MockPromotionService()   // local dev
  if (key === 'mock') return new MockPromotionService()                     // asked for, explicitly
  if ((REAL_KEYS as readonly string[]).includes(key)) return new HttpPromotionService()

  // Set to something unrecognised. The real service is the safe answer: a mock in a deployed build tells
  // people work happened when it did not, which is the worst outcome available here.
  // eslint-disable-next-line no-console
  console.error(
    `[promotion] VITE_PROMOTION_SERVICE="${key}" is not recognised (expected ${REAL_KEYS.join(' | ')} | mock). `
    + 'Using the real service — a mock would silently fake promotions.',
  )
  return new HttpPromotionService()
}

const defaultService = resolvePromotionService()

const PromotionServiceContext = createContext<IPromotionService>(defaultService)

interface PromotionServiceProviderProps {
  children: ReactNode
  /** Override the service instance — used in tests. */
  service?: IPromotionService
}

export function PromotionServiceProvider({
  children,
  service: override,
}: PromotionServiceProviderProps) {
  return (
    <PromotionServiceContext.Provider value={override ?? defaultService}>
      {children}
    </PromotionServiceContext.Provider>
  )
}

export function usePromotionService(): IPromotionService {
  return useContext(PromotionServiceContext)
}
